
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { closeMenu, openMenu } from "../../features/menu/menuSlice";
import Search from "../../components/Search";
import { convertApiToNavData } from "../../helpers/convertApiToNavData";
import PartnerApi from "../../helpers/PartnerApi";
import { useTranslation } from "react-i18next";
import DocProductModal from "../doc/DocProductModal";
import UpdateIcon from "../../../src/icons-svg/update_icon.svg";

const SELECTION_KEY = "doc_product_selection";

const saveSelection = (payload) => {
  try {
    localStorage.setItem(SELECTION_KEY, JSON.stringify(payload));
  } catch {}
};

const norm = (s) => String(s ?? "").toLowerCase().trim();

const filterNavTree = (items, query) => {
  const q = norm(query);
  if (!q) return items || [];

  const walk = (arr) => {
    if (!Array.isArray(arr)) return [];

    const out = [];
    for (const item of arr) {
      const title = norm(item?.title);
      const titleHit = title.includes(q);
      const hasChilds = Array.isArray(item?.childs) && item.childs.length > 0;

      // ✅ Parent / Header eşleşirse altını komple göster
      if ((titleHit && hasChilds) || (item?.isHeader && titleHit)) {
        out.push({ ...item, childs: item.childs });
        continue;
      }

      // child filtrele
      if (hasChilds) {
        const filteredChilds = walk(item.childs);
        if (filteredChilds.length > 0) {
          out.push({ ...item, childs: filteredChilds });
          continue;
        }
      }

      // leaf eşleşirse
      if (!hasChilds && titleHit) out.push(item);
    }
    return out;
  };

  return walk(items);
};

// -------------------- UI LIST --------------------
const NavList = ({ navList, level = 0, onSelect }) => {
  if (!navList || navList.length === 0) return null;
  const isRoot = level === 0;

  return (
    <ul
      style={{
        paddingLeft: isRoot ? 20 : 10,
        listStyleType: "none",
        fontSize: "14px",
      }}
    >
      {navList.map((item) => (
        <li key={item.id ?? item.uuid ?? item.title} className="mb-1">
          {isRoot && item.isHeader ? (
            <div className="mt-2 mb-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded font-bold text-[20px] text-[#314251]">
              {item.title}
            </div>
          ) : item.isHeader ? (
            <strong className="block text-[#314251] font-semibold text-[16px]">
              {item.title}
            </strong>
          ) : item.childs?.length > 0 ? (
            <>
              <strong className="block text-[#314251] font-semibold text-[16px]">
                {item.title}
              </strong>
              <NavList navList={item.childs} level={level + 1} onSelect={onSelect} />
            </>
          ) : (
            <span
              onClick={() => onSelect?.({ uuid: item.uuid, title: item.title })}
              className="inline-block rounded cursor-pointer transition hover:bg-gray-100 dark:hover:bg-gray-700 font-normal hover:font-bold text-[#4C67E9] text-[14px]"
              style={{ userSelect: "none" }}
            >
              {item.title}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
};

const NavigationContent = ({
  selectedProductName,
  selectedVersionName,
  productList,
  loadingProducts,
  errorProducts,
  navData,
  search,
  setSearch,
  onDocSelect,
  onModalOpen,
  modalVisible,
  onModalSubmit,
  onModalClose,
  selectionCommitted,
}) => {
  const { t } = useTranslation();

  const filteredNavData = useMemo(() => filterNavTree(navData, search), [navData, search]);

  const renderMenuContent = () => {
    // ✅ seçim yapılmadan menü yok
    if (!selectionCommitted || modalVisible) {
      return <p className="text-gray-500 text-sm italic">{t("Seçim yapınız...")}</p>;
    }

    if (loadingProducts) return <p className="text-gray-500 text-sm italic">{t("Yükleniyor...")}</p>;
    if (errorProducts) return <p className="text-red-600 text-sm">{errorProducts}</p>;

    if (search && filteredNavData.length === 0)
      return <p className="text-gray-500 text-sm italic">{t("İçerik bulunamadı")}</p>;

    if (!search && navData.length === 0)
      return <p className="text-gray-500 text-sm italic">{t("Henüz menü verisi yok")}</p>;

    return <NavList navList={filteredNavData} onSelect={onDocSelect} />;
  };

  return (
    <div className="flex flex-col h-full bg-[#F0F3F4] w-full max-w-[22rem] min-w-[16rem] lg:relative">
      {/* Ürün/Versiyon Başlık */}
      <div className="flex justify-between items-start px-4 pt-6 text-sm flex-shrink-0">
        <div className="flex flex-wrap gap-x-4 gap-y-1 min-w-0 flex-1">
          <div className="flex items-center">
            <strong className="mr-1 whitespace-nowrap">{t("Ürün")}:</strong>
            <span className="truncate">{selectedProductName || "-"}</span>
          </div>
          <div className="flex items-center">
            <strong className="mr-1 whitespace-nowrap">{t("Versiyon")}:</strong>
            <span className="truncate">{selectedVersionName || "-"}</span>
          </div>
        </div>

        <button onClick={onModalOpen} title={t("Ürün/Versiyon Güncelle")} className="flex-shrink-0 ml-2">
          <img src={UpdateIcon} alt="Güncelle" className="w-5 h-5 object-contain" />
        </button>
      </div>

      {/* Arama */}
      <div className="px-4 py-3 flex-shrink-0">
        <Search
          onSearch={setSearch}
          placeholder={t("Ara...")}
          value={search}
          className="rounded px-2 py-1"
          style={{ backgroundColor: "#ffffff", color: "#000000", border: "1px solid #C4CED8" }}
        />
      </div>

      {/* Menü */}
      <div className="flex-1 overflow-y-auto px-4 pb-2">{renderMenuContent()}</div>

      {/* Modal */}
      <DocProductModal
        visible={modalVisible}
        onClose={onModalClose}
        onSubmit={onModalSubmit}
        productList={productList}
      />
    </div>
  );
};

const Navigation = ({ onDocSelect, onProductVersionChange, onNavDataLoaded }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const { open } = useSelector((state) => state.menu);

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const [search, setSearch] = useState("");
  const [navData, setNavData] = useState([]);

  const [product, setProduct] = useState("");
  const [version, setVersion] = useState("");

  // ✅ sayfa açılır açılmaz modal açık
  const [modalVisible, setModalVisible] = useState(true);

  // ✅ seçim yapılmadan liste çekilmesin + UI boş görünsün
  const [selectionCommitted, setSelectionCommitted] = useState(false);

  // ✅ ÜSTTE YAZANLAR: başlangıçta boş kalacak (cache yazdırma yok!)
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedVersionName, setSelectedVersionName] = useState("");

  const [productList, setProductList] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [errorProducts, setErrorProducts] = useState(null);

  const handleDocSelectInternal = (doc) => {
    onDocSelect?.(doc);
    dispatch(closeMenu());
  };

  // ✅ SADECE ürün listesini çek (navData çekme yok)
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      setErrorProducts(null);
      try {
        const res = await PartnerApi.getDocProductList();
        setProductList(res?.docProductList ?? []);
        // ❗ cache prefill yok → burada hiçbir setProduct/setVersion yok
      } catch (err) {
        setErrorProducts(t("Ürün listesi yüklenirken hata oluştu."));
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [t]);

  const fetchData = async (prod, ver) => {
    if (!prod || !ver) return;

    try {
      const res = await PartnerApi.getDocSearchList({
        product: prod,
        version: ver,
        language: i18n.language,
        status: 2,
        level: 4,
      });

      const converted = convertApiToNavData(res, tRef.current, i18n.language);
      setNavData(converted);
      onNavDataLoaded?.(converted);

      const first = converted.find((item) => !item.isHeader && item.uuid) || null;
      if (first) handleDocSelectInternal(first);
    } catch (err) {
      console.error("Veri alınamadı", err);
      setNavData([]);
      onNavDataLoaded?.([]);
    }
  };

  // ✅ sadece seçim yapıldıktan sonra çek + dil değişince yenile
  useEffect(() => {
    if (!selectionCommitted) return;
    fetchData(product, version);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionCommitted, product, version, i18n.language]);

  const handleModalSubmit = ({ product: p, version: v, productName }) => {
    if (!p || !v) return;

    saveSelection({ product: p, version: v, productName });

    onProductVersionChange?.({ product: p, version: v, productName });

    setProduct(p);
    setVersion(v);

    // ✅ seçim yapıldıktan sonra üstte göster
    setSelectedProductName(productName || "");
    setSelectedVersionName(v || "");

    setSelectionCommitted(true);
    setModalVisible(false);
  };

  const handleModalOpen = () => setModalVisible(true);
  const handleModalClose = () => {
    // kullanıcı kapatırsa bile seçim yoksa menü dolmasın; modalı geri açmak istersen burada true yaparız
    setModalVisible(false);
  };

  // responsive menu
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1280) dispatch(closeMenu());
      else dispatch(openMenu());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  return (
    <>
      <div className="hidden lg:flex flex-col h-full">
        <NavigationContent
          selectedProductName={selectedProductName}
          selectedVersionName={selectedVersionName}
          productList={productList}
          loadingProducts={loadingProducts}
          errorProducts={errorProducts}
          navData={navData}
          search={search}
          setSearch={setSearch}
          onDocSelect={handleDocSelectInternal}
          onModalOpen={handleModalOpen}
          modalVisible={modalVisible}
          onModalSubmit={handleModalSubmit}
          onModalClose={handleModalClose}
          selectionCommitted={selectionCommitted}
        />
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => dispatch(closeMenu())} />
          <div className="relative z-50 w-full max-w-[22rem] bg-[#F0F3F4] shadow-xl h-full">
            <NavigationContent
              selectedProductName={selectedProductName}
              selectedVersionName={selectedVersionName}
              productList={productList}
              loadingProducts={loadingProducts}
              errorProducts={errorProducts}
              navData={navData}
              search={search}
              setSearch={setSearch}
              onDocSelect={handleDocSelectInternal}
              onModalOpen={handleModalOpen}
              modalVisible={modalVisible}
              onModalSubmit={handleModalSubmit}
              onModalClose={handleModalClose}
              selectionCommitted={selectionCommitted}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
